// ─── 📊 Analytics (22/10/2026) ───────────────────────────────────────────────
//
// Mockup «Pacchetto 1 · la sezione Analytics» (v2), approvato: *«1A 2B»*.
// Il gestionale **prende nota** di quattro cose, per Claude Duet (lo studio:
// `docs/funzioni/analytics-duet.md`) e le mostra nella sezione «Analytics»:
//   · da dove arriva una prenotazione (bot, Segreteria, maestro);
//   · le ricerche fatte dal bot e quanti risultati hanno trovato;
//   · i rifiuti del gestionale al bot, col motivo, e i «non lo so ancora»;
//   · la storia di una partita aperta.
// ⛔ Ricerche e rifiuti **senza socio**: si annota cosa si cercava, non chi.
import { z } from 'zod';
/** 🧭 Da dove nasce una prenotazione. `importata` e `sconosciuta` solo per le righe di prima. */
export const ORIGINI_PRENOTAZIONE = ['bot', 'segreteria', 'maestro', 'importata', 'sconosciuta'];
export const ETICHETTE_ORIGINE = {
    bot: 'Bot',
    segreteria: 'Segreteria',
    maestro: 'Maestro',
    importata: 'Importata',
    sconosciuta: 'Non si sa',
};
/** 🔎 Le ricerche del bot che il gestionale annota. */
export const TIPI_DI_RICERCA = [
    'campi_liberi',
    'campi_del_giorno',
    'partite_aperte',
    'lezioni_libere',
];
export const ETICHETTE_RICERCA = {
    campi_liberi: 'Campi liberi',
    campi_del_giorno: 'Campi liberi del giorno',
    partite_aperte: 'Partite aperte',
    lezioni_libere: 'Lezioni libere',
};
/** ✋ I gesti del ponte che possono avere un verdetto. */
export const GESTI_DEL_PONTE = [
    'prenota_campo',
    'disdici',
    'sposta',
    'apri_partita',
    'richiudi_partita',
    'entra_in_partita',
    'esci_dalla_partita',
    'togli_giocatore',
    'aggiungi_giocatore',
    'campo_fisso',
    'rinnovo_no',
    'prenota_lezione',
    'sposta_lezione',
    'carica_certificato',
];
export const ETICHETTE_GESTO_DEL_PONTE = {
    prenota_campo: 'Prenota un campo',
    disdici: 'Disdici',
    sposta: 'Sposta',
    apri_partita: 'Apri la partita',
    richiudi_partita: 'Richiudi la partita',
    entra_in_partita: 'Entra in una partita',
    esci_dalla_partita: 'Esce dalla partita',
    togli_giocatore: 'Toglie un giocatore',
    aggiungi_giocatore: 'Aggiunge un giocatore',
    campo_fisso: 'Campo fisso',
    rinnovo_no: 'Non rinnova il fisso',
    prenota_lezione: 'Prenota una lezione',
    sposta_lezione: 'Sposta una lezione',
    carica_certificato: 'Carica il certificato medico',
};
/** 🎾 I passaggi di una partita aperta. */
export const EVENTI_PARTITA_APERTA = ['aperta', 'chiusa', 'entra', 'esce', 'piena'];
// ── 🤖 Pacchetto 2: quello che sa solo il bot (22/10/2026, *«1A 2A 3B»*) ─────
//
// Il bot lo manda una volta al giorno, di notte, coi totali del giorno prima (1A). Le conversazioni
// no (2A). Inviti e lista d'attesa portano il socio, che la pagina dice col cognome puntato (3B).
// ⚖️ Voci e tipi di avviso sono **chiavi del bot**: una chiave nuova arriva prima della sua
// etichetta, e la pagina la mostra com'è invece di rifiutarla.
/** 🔘 Le voci del bot, come le chiama il socio. */
export const ETICHETTE_VOCE_DEL_BOT = {
    tasto_home: '🏠 Home (il tasto in fondo)',
    tasto_partite: '🎾 Partite (il tasto in fondo)',
    tasto_lezioni: '🎓 Lezioni (il tasto in fondo)',
    tasto_tornei: '🏆 Tornei (il tasto in fondo)',
    tasto_prenota: '📅 Prenota (il tasto di prima)',
    home: '🏠 Home',
    prenota: '📅 Prenota un campo',
    partite: '📋 Le mie partite',
    aperte: '🎾 Partite aperte',
    lezioni: '🎓 Lezioni',
    inviti: '✉️ Inviti a una partita',
    invita_amico: '📨 Invita un amico',
    rubrica: '👥 Rubrica',
    gruppi: '👥 Gruppi',
    notifiche: '🔔 Notifiche',
    richieste: '🔔 Richieste del giorno',
    ricordi: '⏰ Ricordi',
    riepilogo: '📋 Riepilogo',
    conferma: '✅ Conferme di chi organizza',
    sposta: '✏️ Sposta',
    ritardo: '⏱️ Sono in ritardo',
    non_trovati: '🧾 Giocatori non trovati',
    livello: '🎾 Livello di gioco',
    info: 'ℹ️ Info sul Circolo',
    segreteria: '✉️ Scrivi alla Segreteria',
    segnala: '🙋 Domande passate alla Segreteria',
    indietro: '⬅️ Indietro e gruppi del menu',
    porta: '🚪 Prima di entrare',
    registrazione: '🪪 Registrazione',
    altro: 'Altro',
};
/** 🔔 I tipi di avviso del bot. */
export const ETICHETTE_AVVISO_DEL_BOT = {
    disdetta: 'Scadenza della disdetta',
    promemoria: 'Promemoria della partita',
    incompleta: 'Partita da completare',
    segreteria: 'Novità della Segreteria',
    livello: 'Livello di gioco',
    partite_adatte: 'Partite adatte a te',
    disponibilita: 'Le tue disponibilità',
    amici: 'Gli amici',
    suggerimenti: 'Suggerimenti',
    partita: 'A chi organizza (72 ore, vigilia, giorno)',
    richiesta: 'Richiesta del giorno',
    avviso_del_giorno: 'Avviso del giorno',
    ancora: '«Le vuoi ancora?»',
    ricordo: 'Ricordo dell’appuntamento',
    invito_mattino: 'Invito, il mattino della partita',
    ricorda_inviti: 'Ricorda a chi non ha risposto',
};
/** L'etichetta di una chiave del bot, o la chiave stessa se è nuova. */
export const etichettaDelBot = (etichette, chiave) => etichette[chiave] ?? chiave;
export const ESITI_INVITO_DEL_BOT = [
    'accettato',
    'rifiutato',
    'ritirato',
    'posto_preso',
    'non_recapitato',
];
export const ESITI_ATTESA_DEL_BOT = ['entrato', 'uscito', 'chiusa'];
const chiaveDelBot = z.string().regex(/^[a-z][a-z0-9_]{0,39}$/);
const conteggio = z.number().int().min(0).max(1_000_000);
/** ⇢ Quello che il bot manda, una volta per giorno (si può rimandare: il giorno si riscrive). */
export const datiDelBotSchema = z.object({
    date: z.iso.date(),
    taps: z.array(z.object({ item: chiaveDelBot, taps: conteggio })).max(200),
    notices: z.array(z.object({ family: chiaveDelBot, sent: conteggio, touched: conteggio })).max(200),
    invites: z
        .array(z.object({
        code: z.string().min(1).max(100),
        bookingId: z.string().max(100).nullable(),
        inviterId: z.string().max(100),
        inviteeId: z.string().max(100),
        sentAt: z.iso.datetime({ offset: true }),
        outcome: z.enum(ESITI_INVITO_DEL_BOT).nullable(),
        closedAt: z.iso.datetime({ offset: true }).nullable(),
        fromLink: z.boolean(),
    }))
        .max(5000),
    waitlist: z
        .array(z.object({
        bookingId: z.string().max(100),
        memberId: z.string().max(100),
        joinedAt: z.iso.datetime({ offset: true }),
        outcome: z.enum(ESITI_ATTESA_DEL_BOT).nullable(),
        closedAt: z.iso.datetime({ offset: true }).nullable(),
    }))
        .max(5000),
});
export const esitoDatiDelBotSchema = z.object({
    outcome: z.literal('fatto'),
    /** Quanti inviti e quante righe della lista d'attesa il gestionale non ha potuto legare a una partita. */
    scartati: z.number().int(),
});
const partitaDelBot = {
    bookingId: z.uuid(),
    courtName: z.string(),
    startsAt: z.string(),
    /** Il nome di chi organizza, col cognome puntato. */
    organizzatore: z.string().nullable(),
};
/** ⇠ Come la pagina li vede, un giorno. */
export const dalBotSchema = z.object({
    /** Il bot ha già mandato i totali di quel giorno? (Arrivano la notte dopo.) */
    arrivato: z.boolean(),
    tocchi: z.array(z.object({ item: z.string(), taps: z.number().int() })),
    avvisi: z.array(z.object({ family: z.string(), sent: z.number().int(), touched: z.number().int() })),
    inviti: z.array(z.object({
        ...partitaDelBot,
        righe: z.array(z.object({
            /** Chi invita e chi è invitato, col cognome puntato. */
            da: z.string().nullable(),
            a: z.string().nullable(),
            outcome: z.enum(ESITI_INVITO_DEL_BOT).nullable(),
        })),
    })),
    attesa: z.array(z.object({
        ...partitaDelBot,
        righe: z.array(z.object({ chi: z.string().nullable(), outcome: z.enum(ESITI_ATTESA_DEL_BOT).nullable() })),
    })),
});
// ── La pagina «Analytics»: un giorno ─────────────────────────────────────────
export const giornoAnalyticsDomandaSchema = z.object({ date: z.iso.date() });
export const giornoAnalyticsSchema = z.object({
    date: z.iso.date(),
    prenotazioni: z.array(z.object({
        id: z.uuid(),
        createdAt: z.string(),
        courtName: z.string(),
        startsAt: z.string(),
        type: z.string(),
        origin: z.enum(ORIGINI_PRENOTAZIONE),
    })),
    ricerche: z.array(z.object({
        at: z.string(),
        kind: z.enum(TIPI_DI_RICERCA),
        forDate: z.string().nullable(),
        toDate: z.string().nullable(),
        durationMinutes: z.number().int().nullable(),
        results: z.number().int(),
    })),
    rifiuti: z.array(z.object({
        at: z.string(),
        action: z.enum(GESTI_DEL_PONTE),
        outcome: z.enum(['rifiutato', 'non_lo_so']),
        code: z.string().nullable(),
        reason: z.string().nullable(),
    })),
    partiteAperte: z.array(z.object({
        bookingId: z.uuid(),
        courtName: z.string(),
        startsAt: z.string(),
        matchType: z.string().nullable(),
        /** Il nome di chi organizza, col cognome puntato. */
        organizzatore: z.string().nullable(),
        eventi: z.array(z.object({
            at: z.string(),
            event: z.enum(EVENTI_PARTITA_APERTA),
            /** Chi entra o esce, col cognome puntato. */
            chi: z.string().nullable(),
            freePlaces: z.number().int().nullable(),
        })),
    })),
    dalBot: dalBotSchema,
});
//# sourceMappingURL=analytics.js.map